import { CheckOutlined } from "@ant-design/icons";
import { ConfigProvider, Segmented as AntSegmented, type ThemeConfig } from "antd";
import type { SegmentedProps as AntSegmentedProps } from "antd";
import type { SegmentedLabeledOption } from "antd/es/segmented";

/**
 * `choice` is the grey control from the design (Figma "Gjenstander i leveransen", node 1130:3376)
 * for a decision about the customer's items or money: stone removal, payout or gift card. The
 * selected item is white and carries a check, so the choice reads without relying on colour.
 * Filters keep antd's quiet `default`.
 */
type SegmentedVariant = "default" | "choice";

type SegmentedProps<T> = AntSegmentedProps<T> & { variant?: SegmentedVariant };

// antd draws the selected item's shadow from boxShadowTertiary; the inset ring is its 1px border.
const SELECTED_ITEM_SHADOW = "0 0 0 1px #D1CCBF, 0 1px 3px 0 rgba(0, 0, 0, 0.05)";

const choiceTheme: ThemeConfig = {
  token: { boxShadowTertiary: SELECTED_ITEM_SHADOW },
  components: {
    Segmented: {
      trackBg: "#E3E2DF",
      trackPadding: 2,
      itemColor: "#000000",
      itemHoverColor: "#000000",
      itemHoverBg: "rgba(0, 0, 0, 0.04)",
      itemActiveBg: "rgba(0, 0, 0, 0.08)",
      itemSelectedBg: "#FFFFFF",
      itemSelectedColor: "#262626",
    },
  },
};

// antd paints every disabled item, the selected one included, in one text colour; the check and
// the white item still mark the choice once it is locked.
const lockedChoiceTheme: ThemeConfig = {
  token: { boxShadowTertiary: SELECTED_ITEM_SHADOW, colorTextDisabled: "#8A8378" },
  components: {
    Segmented: { trackBg: "#EEECE8", trackPadding: 2, itemSelectedBg: "#FFFFFF" },
  },
};

export function Segmented<T>({ variant = "default", ...props }: SegmentedProps<T>) {
  if (variant === "default") return <AntSegmented<T> {...props} />;
  const options = props.options.map((option) => {
    const item: SegmentedLabeledOption<T> =
      typeof option === "object" && option !== null && "value" in option
        ? (option as SegmentedLabeledOption<T>)
        : { value: option as T, label: String(option) };
    if (item.value !== props.value) return option;
    return {
      ...item,
      icon: <CheckOutlined />,
      label: <span style={{ fontWeight: 500 }}>{item.label}</span>,
    };
  });
  return (
    <ConfigProvider theme={props.disabled ? lockedChoiceTheme : choiceTheme}>
      <AntSegmented<T>
        {...props}
        options={options}
        style={{ border: `1px solid ${props.disabled ? "#D1CCBF" : "#000000"}`, ...props.style }}
      />
    </ConfigProvider>
  );
}
